<script setup>
// ACESSO-01 (L9b): o "Onde atua" da pessoa, no modal de editar agente, com a chave ACESSO_GRANULAR ligada (quadro 6 do
// mock v3, SPEC S4): as caixas, os pipelines e os apps em que ela atua, em texto, cada lista com "Alterar".
//
// - Ver é do `people:manage`, que quem mostra o componente confere. Mudar cada lista é da gestão do módulo dela
//   (`inboxes:manage`, `kanban:manage` e `powerapps:manage`), como no servidor: sem o escopo, o "Alterar" não aparece.
// - O seletor salva na hora, com a lista inteira, sem esperar o Salvar do modal. Uma mudança espera a anterior, e a
//   resposta traz só a lista que mudou. A falha diz a frase do servidor (a recusa do acesso, ou o 422 de quem tira o
//   administrador de uma caixa), e no fim o seletor fica com o que o servidor tem.
// - O administrador da conta atua em todas as caixas, e as caixas dele não têm "Alterar": o servidor recusaria tudo. Os
//   pipelines têm "Todos os pipelines", que deixa a lista de lado. Quem vê todos os pipelines ou apps pelo acesso que
//   tem (`ve_todos`) lê isso, porque a lista não muda nada para ele.
// - Gravada uma mudança, o título diz "Salvo. Já vale para a pessoa.": o "Cancelar" do modal não a desfaz, e sem esse
//   aviso nada dizia que ela já valia (revisão de desenho, Task 9.6). A falha tira o aviso.
// - O seletor é uma lista de caixas de seleção, que o teclado alcança (Tab e espaço), com uma busca a partir de 8
//   itens. Era o `TagMultiSelectComboBox`, que abre por um clique numa <div> e escolhe por clique num <li>: quem usa
//   só o teclado não mudava onde a pessoa atua (revisão de desenho, Task 9.6).
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAcesso } from 'dashboard/composables/useAcesso';
import AcessoAPI from 'dashboard/api/acesso';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { fraseDaFalha, segurarOEnter } from './acessoDaPessoa';

const props = defineProps({
  // O id do usuário, como em /agents/:id.
  pessoaId: { type: Number, required: true },
});

const { t } = useI18n();
const { pode } = useAcesso();

// A partir de quantos itens a lista aberta ganha a busca.
const BUSCA_A_PARTIR_DE = 8;

const onde = ref(null);
const erro = ref('');
const aberta = ref('');
const busca = ref('');
const salvo = ref(false);
// O que cada seletor mostra: os ids escolhidos e o "Todos os pipelines".
const escolhidas = ref({ caixas: [], funis: [], apps: [], todos: false });

const emQueAtua = bloco => bloco.lista.filter(item => item.atua);
const nomes = bloco =>
  emQueAtua(bloco)
    .map(item => item.nome)
    .join(', ');
const ids = bloco => emQueAtua(bloco).map(item => item.id);

const sincronizar = () => {
  const { caixas, funis, apps } = onde.value;
  escolhidas.value = {
    caixas: ids(caixas),
    funis: ids(funis),
    apps: ids(apps),
    todos: funis.todos,
  };
};

const textoDasCaixas = caixas => {
  if (caixas.administrador) {
    return t('AGENT_MGMT.ACESSO.ONDE_ATUA.TODAS_COMO_ADMINISTRADOR');
  }
  return nomes(caixas) || t('AGENT_MGMT.ACESSO.ONDE_ATUA.NENHUMA');
};
const textoDosFunis = funis => {
  if (funis.todos) return t('AGENT_MGMT.ACESSO.ONDE_ATUA.TODOS');
  if (funis.ve_todos) return t('AGENT_MGMT.ACESSO.ONDE_ATUA.TODOS_PELO_ACESSO');
  return nomes(funis) || t('AGENT_MGMT.ACESSO.ONDE_ATUA.NENHUM');
};
const textoDosApps = apps => {
  if (apps.ve_todos) return t('AGENT_MGMT.ACESSO.ONDE_ATUA.TODOS_PELO_ACESSO');
  return nomes(apps) || t('AGENT_MGMT.ACESSO.ONDE_ATUA.NENHUM');
};

const linhas = computed(() => {
  if (!onde.value) return [];
  const { caixas, funis, apps } = onde.value;
  const opcoes = bloco =>
    bloco.lista.map(item => ({ value: item.id, label: item.nome }));
  return [
    {
      chave: 'caixas',
      rotulo: t('AGENT_MGMT.ACESSO.ONDE_ATUA.CAIXAS'),
      texto: textoDasCaixas(caixas),
      opcoes: opcoes(caixas),
      escolher: t('AGENT_MGMT.ACESSO.ONDE_ATUA.ESCOLHER_CAIXAS'),
      podeAlterar: pode('inboxes:manage') && !caixas.administrador,
      trocar: escolhidos => AcessoAPI.trocarCaixas(props.pessoaId, escolhidos),
    },
    {
      chave: 'funis',
      rotulo: t('AGENT_MGMT.ACESSO.ONDE_ATUA.PIPELINES'),
      texto: textoDosFunis(funis),
      opcoes: opcoes(funis),
      escolher: t('AGENT_MGMT.ACESSO.ONDE_ATUA.ESCOLHER_PIPELINES'),
      podeAlterar: pode('kanban:manage'),
      trocar: escolhidos =>
        AcessoAPI.trocarFunis(props.pessoaId, { ids: escolhidos }),
    },
    {
      chave: 'apps',
      rotulo: t('AGENT_MGMT.ACESSO.ONDE_ATUA.POWERAPPS'),
      texto: textoDosApps(apps),
      opcoes: opcoes(apps),
      escolher: t('AGENT_MGMT.ACESSO.ONDE_ATUA.ESCOLHER_POWERAPPS'),
      podeAlterar: pode('powerapps:manage'),
      trocar: escolhidos => AcessoAPI.trocarApps(props.pessoaId, escolhidos),
    },
  ];
});

const alternar = chave => {
  aberta.value = aberta.value === chave ? '' : chave;
  busca.value = '';
};

const visiveis = linha => {
  const termo = busca.value.trim().toLocaleLowerCase();
  if (!termo) return linha.opcoes;
  return linha.opcoes.filter(opcao =>
    opcao.label.toLocaleLowerCase().includes(termo)
  );
};

// Uma mudança por vez, na ordem em que a pessoa as fez: a última vale. Cada resposta traz a lista que mudou.
let fila = Promise.resolve();
let pendentes = 0;
const mudar = pedido => {
  pendentes += 1;
  fila = fila
    .then(async () => {
      try {
        const { data } = await pedido();
        onde.value = { ...onde.value, ...data };
        salvo.value = true;
      } catch (falha) {
        salvo.value = false;
        useAlert(
          fraseDaFalha(falha, t('AGENT_MGMT.ACESSO.ONDE_ATUA.ERRO_AO_SALVAR'))
        );
      }
    })
    .then(() => {
      pendentes -= 1;
      if (!pendentes) sincronizar();
    });
};

const trocarLista = (linha, escolhidos) => {
  const copia = [...escolhidos];
  mudar(() => linha.trocar(copia));
};

// A caixa marcada entra no fim da lista, e a desmarcada sai; a lista inteira vai ao servidor, como antes.
const marcar = (linha, id, marcado) => {
  const atuais = escolhidas.value[linha.chave];
  const novas = marcado ? [...atuais, id] : atuais.filter(cada => cada !== id);
  escolhidas.value[linha.chave] = novas;
  trocarLista(linha, novas);
};

const trocarTodos = todos => {
  escolhidas.value.todos = todos;
  mudar(() => AcessoAPI.trocarFunis(props.pessoaId, { todos }));
};

onMounted(async () => {
  try {
    const { data } = await AcessoAPI.ondeAtua(props.pessoaId);
    onde.value = data;
    sincronizar();
  } catch (falha) {
    erro.value = fraseDaFalha(
      falha,
      t('AGENT_MGMT.ACESSO.ONDE_ATUA.ERRO_AO_CARREGAR')
    );
  }
});
</script>

<template>
  <section class="flex flex-col" data-onde-atua @keydown.enter="segurarOEnter">
    <div class="flex flex-wrap items-baseline justify-between gap-x-3">
      <h3 class="mb-0 text-sm font-medium leading-7 text-n-slate-12">
        {{ t('AGENT_MGMT.ACESSO.ONDE_ATUA.TITULO') }}
      </h3>
      <p
        class="flex items-center gap-1 mb-0 text-xs text-n-slate-11"
        aria-live="polite"
        data-salvo
      >
        <template v-if="salvo">
          <span
            class="i-lucide-check size-3.5 shrink-0 text-n-teal-11"
            aria-hidden="true"
          />
          <span class="font-semibold text-n-slate-12">
            {{ t('AGENT_MGMT.ACESSO.SALVO') }}
          </span>
          {{ t('AGENT_MGMT.ACESSO.JA_VALE') }}
        </template>
      </p>
    </div>
    <p v-if="erro" class="py-2 mb-0 text-sm text-n-slate-11" data-erro>
      {{ erro }}
    </p>
    <p v-else-if="!onde" class="py-2 mb-0 text-sm text-n-slate-11">
      {{ t('AGENT_MGMT.ACESSO.ONDE_ATUA.CARREGANDO') }}
    </p>
    <div
      v-for="linha in linhas"
      :key="linha.chave"
      class="border-t border-n-weak"
      :data-lista="linha.chave"
    >
      <div
        class="grid grid-cols-[6rem_minmax(0,1fr)_auto] items-baseline gap-2.5 py-2 text-sm"
      >
        <span class="text-n-slate-11">{{ linha.rotulo }}</span>
        <span class="min-w-0 break-words text-n-slate-12" data-texto>
          {{ linha.texto }}
        </span>
        <Button
          v-if="linha.podeAlterar"
          type="button"
          variant="link"
          color="slate"
          size="sm"
          :label="
            aberta === linha.chave
              ? t('AGENT_MGMT.ACESSO.ONDE_ATUA.FECHAR')
              : t('AGENT_MGMT.ACESSO.ONDE_ATUA.ALTERAR')
          "
          :aria-expanded="aberta === linha.chave ? 'true' : 'false'"
          data-alterar
          @click="alternar(linha.chave)"
        />
      </div>
      <div
        v-if="aberta === linha.chave"
        class="flex flex-col gap-2 pb-3"
        data-seletor
      >
        <label
          v-if="linha.chave === 'funis'"
          class="flex items-center gap-2 font-normal"
        >
          <Checkbox
            :model-value="escolhidas.todos"
            data-todos
            @update:model-value="trocarTodos"
          />
          {{ t('AGENT_MGMT.ACESSO.ONDE_ATUA.TODOS_OS_PIPELINES') }}
        </label>
        <template v-if="linha.chave !== 'funis' || !escolhidas.todos">
          <Input
            v-if="linha.opcoes.length > BUSCA_A_PARTIR_DE"
            v-model="busca"
            type="search"
            size="sm"
            :placeholder="t('AGENT_MGMT.ACESSO.ONDE_ATUA.BUSCAR')"
            custom-input-class="placeholder:!text-n-slate-11 dark:placeholder:!text-n-slate-11"
            data-busca
          />
          <fieldset
            class="flex flex-col p-0 m-0 overflow-y-auto border-0 max-h-56"
          >
            <legend class="sr-only">{{ linha.escolher }}</legend>
            <label
              v-for="opcao in visiveis(linha)"
              :key="opcao.value"
              class="flex items-center gap-2 py-1 mb-0 font-normal cursor-pointer"
            >
              <Checkbox
                :model-value="escolhidas[linha.chave].includes(opcao.value)"
                :data-opcao="opcao.value"
                @update:model-value="
                  marcado => marcar(linha, opcao.value, marcado)
                "
              />
              <span class="min-w-0 text-sm truncate text-n-slate-12">
                {{ opcao.label }}
              </span>
            </label>
            <p
              v-if="!visiveis(linha).length"
              class="py-1 mb-0 text-sm text-n-slate-11"
              data-nada
            >
              {{
                t('AGENT_MGMT.ACESSO.ONDE_ATUA.NADA_COM', {
                  termo: busca.trim(),
                })
              }}
            </p>
          </fieldset>
        </template>
      </div>
    </div>
  </section>
</template>
